import { create } from 'zustand';
import { readBool, writeBool } from '@/shared/storage';
import {
  hasAmcUiSettings,
  readAmcUiSetting,
  writeAmcUiSetting,
  subscribeAmcUiSettings,
} from '@/shared/amc-ui-setting';

// Persisted layout prefs + one transient runtime flag:
//   COLLAPSED_KEY            — expanded (240px) vs the thin icon rail (44px). A
//                              transient in-session fold; a sliver always stays
//                              clickable. Only meaningful in PINNED mode.
//   HIDDEN_KEY               — whether the persistent sidebar is PINNED. When
//                              `hidden` is false the sidebar lives permanently
//                              in AMC's sub-sidebar slot ("Show sidebar" ON).
//                              When `hidden` is true (the Superhuman default)
//                              the slot is empty and the nav is reached through
//                              a hamburger that opens the overlay drawer — see
//                              `overlayOpen`. The Settings "Show sidebar" toggle
//                              is `checked={!hidden}`. Defaults to true so a
//                              fresh install opens in the Superhuman overlay
//                              mode; an existing user's persisted choice wins.
//   LABELS_COLLAPSED_KEY     — fold the user-labels list inside the expanded
//                              body so a long label catalog doesn't dominate
//                              the nav. Only applies to the expanded variant;
//                              the rail's tag-glyph popup is unaffected.
//   READING_PANE_VISIBLE_KEY — every thread-list page (Inbox/labels/Snoozed/
//                              Follow-ups) renders as 2-pane (list + reading
//                              pane) when true, single-column (full-width list,
//                              click → /thread/:id) when false. Default true.
//   CONTACT_PANE_VISIBLE_KEY — whether the right-hand contact rail (Superhuman's
//                              contact details: photo, role, recent emails) is
//                              shown beside an open conversation. Opt-in: turned
//                              on via the Settings "Contact pane" toggle, and off
//                              again via that toggle, the in-pane close button, or
//                              the "Show/Hide Contact Pane" palette command.
//                              Default FALSE — off unless the user turns it on
//                              (both view presets also leave it off); an existing
//                              user's persisted choice still wins.
//
// `overlayOpen` is deliberately NOT persisted: in overlay mode the drawer's
// resting state is always closed (just the hamburger). It is a transient,
// session-only flag flipped by the hamburger and cleared on mouse-leave /
// backdrop click. Pinning the sidebar (`setHidden(false)`) also clears it.
const COLLAPSED_KEY = 'supermail:nav-collapsed';
const HIDDEN_KEY = 'supermail:nav-hidden';
const LABELS_COLLAPSED_KEY = 'supermail:labels-collapsed';
const READING_PANE_VISIBLE_KEY = 'supermail:reading-pane-visible';
const CONTACT_PANE_VISIBLE_KEY = 'supermail:contact-pane-visible';

interface SidebarState {
  collapsed: boolean;
  hidden: boolean;
  // Transient: is the overlay drawer expanded right now? Only meaningful while
  // `hidden` is true (overlay mode). Never persisted.
  overlayOpen: boolean;
  labelsCollapsed: boolean;
  readingPaneVisible: boolean;
  contactPaneVisible: boolean;
  toggle: () => void;
  setCollapsed: (collapsed: boolean) => void;
  setHidden: (hidden: boolean) => void;
  openOverlay: () => void;
  closeOverlay: () => void;
  toggleOverlay: () => void;
  toggleLabels: () => void;
  setLabelsCollapsed: (labelsCollapsed: boolean) => void;
  toggleReadingPane: () => void;
  setReadingPaneVisible: (visible: boolean) => void;
  toggleContactPane: () => void;
  setContactPaneVisible: (visible: boolean) => void;
}

export const useSidebarStore = create<SidebarState>((set) => ({
  collapsed: readBool(COLLAPSED_KEY),
  // Default true → fresh installs open in Superhuman-style overlay mode. A
  // returning user's persisted '0'/'1' still wins (readBool only falls back to
  // the default when the key is absent).
  hidden: hasAmcUiSettings()
    ? (readAmcUiSetting('supermailNavHidden') ?? true)
    : readBool(HIDDEN_KEY, true),
  overlayOpen: false,
  labelsCollapsed: readBool(LABELS_COLLAPSED_KEY),
  readingPaneVisible: hasAmcUiSettings()
    ? (readAmcUiSetting('supermailReadingPaneVisible') ?? true)
    : readBool(READING_PANE_VISIBLE_KEY, true),
  // Default FALSE → the contact rail is opt-in; a returning user who turned it on
  // (persisted '1') still gets it (readBool only falls back when the key is absent).
  contactPaneVisible: hasAmcUiSettings()
    ? (readAmcUiSetting('supermailContactPaneVisible') ?? false)
    : readBool(CONTACT_PANE_VISIBLE_KEY, false),
  toggle: () =>
    set((s) => {
      const next = !s.collapsed;
      writeBool(COLLAPSED_KEY, next);
      return { collapsed: next };
    }),
  setCollapsed: (collapsed) => {
    writeBool(COLLAPSED_KEY, collapsed);
    set({ collapsed });
  },
  setHidden: (hidden) => {
    if (hasAmcUiSettings()) {
      writeAmcUiSetting('supermailNavHidden', hidden);
    } else {
      writeBool(HIDDEN_KEY, hidden);
    }
    // Pinning the sidebar makes the transient drawer meaningless — close it so
    // it can't linger as a stale overlay behind the now-pinned column.
    set(hidden ? { hidden } : { hidden, overlayOpen: false });
  },
  openOverlay: () => set({ overlayOpen: true }),
  closeOverlay: () => set({ overlayOpen: false }),
  toggleOverlay: () => set((s) => ({ overlayOpen: !s.overlayOpen })),
  toggleLabels: () =>
    set((s) => {
      const next = !s.labelsCollapsed;
      writeBool(LABELS_COLLAPSED_KEY, next);
      return { labelsCollapsed: next };
    }),
  setLabelsCollapsed: (labelsCollapsed) => {
    writeBool(LABELS_COLLAPSED_KEY, labelsCollapsed);
    set({ labelsCollapsed });
  },
  toggleReadingPane: () =>
    set((s) => {
      const next = !s.readingPaneVisible;
      if (hasAmcUiSettings()) {
        writeAmcUiSetting('supermailReadingPaneVisible', next);
      } else {
        writeBool(READING_PANE_VISIBLE_KEY, next);
      }
      return { readingPaneVisible: next };
    }),
  setReadingPaneVisible: (visible) => {
    if (hasAmcUiSettings()) {
      writeAmcUiSetting('supermailReadingPaneVisible', visible);
    } else {
      writeBool(READING_PANE_VISIBLE_KEY, visible);
    }
    set({ readingPaneVisible: visible });
  },
  toggleContactPane: () =>
    set((s) => {
      const next = !s.contactPaneVisible;
      if (hasAmcUiSettings()) {
        writeAmcUiSetting('supermailContactPaneVisible', next);
      } else {
        writeBool(CONTACT_PANE_VISIBLE_KEY, next);
      }
      return { contactPaneVisible: next };
    }),
  setContactPaneVisible: (visible) => {
    if (hasAmcUiSettings()) {
      writeAmcUiSetting('supermailContactPaneVisible', visible);
    } else {
      writeBool(CONTACT_PANE_VISIBLE_KEY, visible);
    }
    set({ contactPaneVisible: visible });
  },
}));

subscribeAmcUiSettings((s) =>
  useSidebarStore.setState({
    hidden: s.supermailNavHidden,
    readingPaneVisible: s.supermailReadingPaneVisible,
    contactPaneVisible: s.supermailContactPaneVisible,
  })
);
